import type { CSSProperties } from 'react';

/**
 * Logo DONGNIME — SELALU dari aset PNG resmi di `public/assets/branding/`.
 *
 * JANGAN menggambar ulang logo dengan CSS/SVG/font/ikon, dan jangan memberi
 * filter yang mengubah warna aset. Bentuk, proporsi, gradien emas dan efek
 * metalik harus persis sama dengan aset yang diberikan.
 *
 * Kenapa ada dua sumber per varian:
 * pada aset versi terang, huruf "DONG" berwarna gelap sehingga hilang di atas
 * latar hitam. Karena itu tiap varian punya pasangan terang/gelap sendiri.
 * `emblem` netral (emas + perak) sehingga dipakai untuk kedua tema.
 */

export type LogoVariant = 'full' | 'horizontal' | 'emblem' | 'wordmark';
export type LogoTheme = 'light' | 'dark';
export type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const BASE = '/assets/branding';

const SRC_LIGHT: Record<LogoVariant, string> = {
  full: `${BASE}/dongnime-full-light.png`,
  horizontal: `${BASE}/dongnime-horizontal-light.png`,
  emblem: `${BASE}/dongnime-emblem.png`,
  wordmark: `${BASE}/dongnime-wordmark-light.png`,
};

const SRC_DARK: Record<LogoVariant, string> = {
  // Lockup gelap kotak: "DONG" putih, latar tile menyatu dgn background app.
  full: `${BASE}/dongnime-square-dark.png`,
  // Lockup mendatar resmi versi gelap: "DONG" putih, latar TRANSPARAN,
  // resolusi tinggi (1969x527). Ini yang dipakai di header.
  horizontal: `${BASE}/dongnime-horizontal-dark.png`,
  // Emblem netral — aman di terang maupun gelap.
  emblem: `${BASE}/dongnime-emblem.png`,
  // Belum ada wordmark LEPAS versi gelap; pakai lockup mendatar gelap
  // (memuat emblem + wordmark) agar tetap terbaca.
  wordmark: `${BASE}/dongnime-horizontal-dark.png`,
};

/** Rasio asli tiap aset — dipakai agar tidak terjadi layout shift. */
const RATIO_LIGHT: Record<LogoVariant, string> = {
  full: '526 / 535',
  horizontal: '469 / 177',
  emblem: '372 / 364',
  wordmark: '542 / 155',
};

const RATIO_DARK: Record<LogoVariant, string> = {
  full: '257 / 258',
  horizontal: '1969 / 527',
  emblem: '372 / 364',
  wordmark: '1969 / 527',
};

/** Tinggi render (px). Lebar selalu otomatis agar rasio asli terjaga. */
const HEIGHT: Record<LogoVariant, Record<LogoSize, number>> = {
  full: { xs: 64, sm: 96, md: 140, lg: 200, xl: 280 },
  horizontal: { xs: 24, sm: 30, md: 38, lg: 56, xl: 78 },
  emblem: { xs: 20, sm: 26, md: 32, lg: 48, xl: 72 },
  wordmark: { xs: 16, sm: 20, md: 26, lg: 36, xl: 52 },
};

interface Props {
  variant?: LogoVariant;
  theme?: LogoTheme;
  size?: LogoSize;
  className?: string;
  style?: CSSProperties;
  /** Override tinggi (px) bila butuh ukuran di luar skala. */
  height?: number;
  /** Logo dekoratif (nama merek sudah ada di dekatnya) → alt dikosongkan. */
  decorative?: boolean;
}

export function DongnimeLogo({
  variant = 'emblem',
  theme = 'dark',
  size = 'md',
  className,
  style,
  height,
  decorative = false,
}: Props) {
  const dark = theme === 'dark';
  const src = dark ? SRC_DARK[variant] : SRC_LIGHT[variant];
  const ratio = dark ? RATIO_DARK[variant] : RATIO_LIGHT[variant];
  const h = height ?? HEIGHT[variant][size];

  return (
    <img
      src={src}
      alt={decorative ? '' : 'DONGNIME'}
      className={`dongnime-logo${className ? ` ${className}` : ''}`}
      style={{
        // Dibaca oleh .dongnime-logo di index.css (termasuk breakpoint 360px).
        ['--dn-logo-h' as string]: `${h}px`,
        aspectRatio: ratio,
        ...style,
      } as CSSProperties}
      draggable={false}
      {...(decorative ? { 'aria-hidden': true } : {})}
    />
  );
}
